import { groupedActivity, needsAttention } from '../lib/collaborationViews';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon,
  ChevronRightIcon,
  ClockIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useTicker } from '../hooks/useTicker';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { haptic } from '../lib/haptics';
import { formatDuration, formatRelativeTime, runningDuration } from '../lib/time';
import { SkeletonCards } from '../components/SkeletonCards';
import { Avatar } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { QueryError } from '../components/QueryError';
import { DayWrapCard } from '../components/DayWrapCard';
import { RecapCard } from '../components/RecapCard';
import { FeedRow } from '../lib/feed';
import type { FeedItem, PlanItem, StatusMode } from '../lib/types';
import { PageHeader } from '../components/ui/PageHeader';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { DomovLayout, DOMOV_WIDE_QUERY } from '../components/DomovLayout';
import { useMediaQuery } from '../hooks/useMediaQuery';

const TABS = [
  { key: 'dnes', label: 'Dnes' },
  { key: 'aktivita', label: 'Aktivita' },
] as const;

// Local calendar day -> local-midnight ISO bounds.
const localDayBounds = (dayKey: string): { start: string; end: string } => {
  const [year, month, day] = dayKey.split('-').map((part) => Number(part));
  const startDate = new Date(year, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0);
  const endDate = new Date(year, (month ?? 1) - 1, day ?? 1, 23, 59, 59, 999);
  return { start: startDate.toISOString(), end: endDate.toISOString() };
};

// Local YYYY-MM-DD for a Date.
const localDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatPlanTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });

// A colleague's "running" session whose device went dark: freeze the shown
// duration at the moment they were last seen instead of ticking forever.
// Missing presence fields are treated as online (no behavior change).
const frozenDuration = (startedAt: string, lastSeenAt: string): number =>
  Math.max(
    0,
    Math.floor(
      (new Date(normalizeIso(lastSeenAt)).getTime() - new Date(normalizeIso(startedAt)).getTime()) /
        1000,
    ),
  );

// Domov = one domain, two views: „Dnes" (čo sa deje teraz a čo mám dnes) and
// „Aktivita" (full day-grouped feed). Tab survives in the URL so
// /?tab=aktivita is linkable (ActivityBell + old /activity redirect).
export const DashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'aktivita' ? 'aktivita' : 'dnes';
  const queryClient = useQueryClient();

  // Pull-to-refresh (touch-only, installed PWA) — invalidate the page's main
  // queries; invalidateQueries awaits the refetch of the active ones.
  const ptr = usePullToRefresh(async () => {
    haptic();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['plans'] }),
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['events'] }),
    ]);
  });

  // Na širokom monitore má „Dnes“ dva stĺpce, a teda aj širšiu stránku;
  // „Aktivita“ je jeden prúd na čítanie a ostáva v šírke zoznamu.
  const wide = useMediaQuery(DOMOV_WIDE_QUERY) && tab === 'dnes';

  return (
    <div
      {...ptr.containerProps}
      className={`${wide ? 'page-grid' : 'page-list'} relative space-y-5`}
    >
      {/* overlay — never participates in layout, so the page doesn't jump */}
      {ptr.indicatorVisible ? (
        <div
          className="pointer-events-none absolute inset-x-0 -top-3 z-10 flex justify-center"
          aria-hidden="true"
        >
          <span
            className={`h-6 w-6 rounded-full border-2 border-brand-500 border-t-transparent ${
              ptr.refreshing ? 'animate-spin' : ''
            }`}
            style={
              ptr.refreshing
                ? undefined
                : {
                    opacity: ptr.progress,
                    transform: `scale(${0.5 + ptr.progress * 0.5}) rotate(${ptr.progress * 270}deg)`,
                  }
            }
          />
        </div>
      ) : null}
      <PageHeader title="Domov" subtitle="Čo sa deje teraz a čo máš dnes robiť." />

      <SegmentedControl
        ariaLabel="Domov"
        className="rise"
        items={TABS}
        value={tab}
        onChange={(key) => setSearchParams(key === 'dnes' ? {} : { tab: key }, { replace: true })}
      />

      {/* Keyed by tab so the content softly re-enters on each switch. */}
      <div key={tab} className="tab-fade">
        {tab === 'dnes' ? (
          <DnesTab onShowActivity={() => setSearchParams({ tab: 'aktivita' }, { replace: true })} />
        ) : (
          <AktivitaTab refreshing={ptr.refreshing} />
        )}
      </div>
    </div>
  );
};

/* --- Tab Dnes ------------------------------------------------------------- */

type TeamTodayRow = {
  userId: number;
  name: string;
  avatarUrl: string | null;
  statusMode: StatusMode;
  statusColor: string | null;
  online: boolean;
  live: {
    projectName: string;
    startedAt: string;
    // Presence of the session's worker — offline live rows render paused.
    workerOnline: boolean | undefined;
    workerLastSeenAt: string | null | undefined;
  } | null;
  plan: PlanItem | null;
  planCount: number;
};

const DnesTab = ({ onShowActivity }: { onShowActivity: () => void }) => {
  const { user } = useAuth();
  useTicker(1000);

  const todayKey = useMemo(() => localDayKey(new Date()), []);
  const todayBounds = useMemo(() => localDayBounds(todayKey), [todayKey]);

  // Polling budget for the whole tab: projects (live sessions + presence-ish
  // „dnes" data) + feed. Everything else relies on staleTime + window focus.
  const projectsQuery = useQuery({
    queryKey: ['projects', false],
    queryFn: () => api.projectsList(false),
    refetchInterval: 15000,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => api.usersList(),
    staleTime: 60_000,
  });
  const todayPlansQuery = useQuery({
    queryKey: ['plans', 'dashboard', 'today', todayKey],
    queryFn: () => api.plansList({ from: todayBounds.start, to: todayBounds.end }),
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', 'dashboard', 'open'],
    queryFn: () => api.tasksList(),
    staleTime: 60_000,
  });
  // Shares the ActivityBell query key, so Domov + bell poll the feed only once.
  const eventsQuery = useQuery({
    queryKey: ['events', 'bell'],
    queryFn: () => api.eventsList({ limit: 10 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const projects = projectsQuery.data?.items || [];
  const users = usersQuery.data?.items || [];
  const tasks = tasksQuery.data?.items || [];
  const feedItems = (eventsQuery.data?.items || []).slice(0, 5);

  const todayPlans = useMemo(
    () =>
      (todayPlansQuery.data?.items || [])
        .slice()
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [todayPlansQuery.data],
  );

  // Tím dnes: one row per person who is live or has a plan today; live first.
  // Every worker on every project counts (multiple people can run sessions on
  // one project) — deduped by user, since a user has max 1 running session.
  const teamToday = useMemo<TeamTodayRow[]>(() => {
    const liveByUser = new Map<number, NonNullable<TeamTodayRow['live']>>();
    for (const project of projects) {
      for (const worker of project.workers) {
        if (!liveByUser.has(worker.userId)) {
          liveByUser.set(worker.userId, {
            projectName: project.name,
            startedAt: worker.startedAt,
            workerOnline: worker.online,
            workerLastSeenAt: worker.lastSeenAt,
          });
        }
      }
    }
    const plansByUser = new Map<number, PlanItem[]>();
    for (const plan of todayPlans) {
      const list = plansByUser.get(plan.userId) || [];
      list.push(plan);
      plansByUser.set(plan.userId, list);
    }
    const rows = users
      .filter((member) => member.active)
      .map((member) => {
        const plans = plansByUser.get(member.id) || [];
        return {
          userId: member.id,
          name: member.name,
          avatarUrl: member.avatarUrl,
          statusMode: member.statusMode,
          statusColor: member.statusColor,
          online: member.online,
          live: liveByUser.get(member.id) || null,
          plan: plans[0] ?? null,
          planCount: plans.length,
        };
      })
      .filter((row) => row.live || row.plan);
    rows.sort((a, b) => {
      if (Boolean(a.live) !== Boolean(b.live)) {
        return a.live ? -1 : 1;
      }
      if (a.live && b.live) {
        return a.live.startedAt.localeCompare(b.live.startedAt);
      }
      return (a.plan?.startTime || '').localeCompare(b.plan?.startTime || '');
    });
    return rows;
  }, [users, projects, todayPlans]);

  // One live person = one live row; a user has max 1 running session.
  const liveCount = useMemo(
    () => new Set(projects.flatMap((project) => project.workers.map((w) => w.userId))).size,
    [projects],
  );

  // Moje dnešky: my open tasks due today or overdue.
  const myTasks = useMemo(() => {
    const endOfToday = new Date(todayBounds.end).getTime();
    return tasks
      .filter(
        (task) => task.userId === user?.id && !task.completed && !task.archivedAt && task.dueAt,
      )
      .filter((task) => new Date(task.dueAt as string).getTime() <= endOfToday)
      .sort((a, b) => new Date(a.dueAt || 0).getTime() - new Date(b.dueAt || 0).getTime());
  }, [tasks, user?.id, todayBounds.end]);

  const myPlans = useMemo(
    () => todayPlans.filter((plan) => plan.userId === user?.id),
    [todayPlans, user?.id],
  );

  const mySession = useMemo(() => {
    for (const project of projects) {
      const mine = project.workers.find((w) => w.userId === user?.id);
      if (mine) {
        return { projectName: project.name, startedAt: mine.startedAt };
      }
    }
    return null;
  }, [projects, user?.id]);

  const formatDueLabel = (iso: string): string => {
    const due = new Date(iso);
    return localDayKey(due) === todayKey
      ? due.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
      : due.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit' });
  };

  const now = Date.now();

  const summary = (
    <>
      <RecapCard />
      {/* How today turned out — appears from the afternoon on, once there is
          something to look back at. */}
      <DayWrapCard />
    </>
  );

  const team = (
    /* --- Tím dnes -------------------------------------------------------- */
    <section className="rise space-y-2">
      <div className="flex items-center gap-2 px-1">
        <h2 className="t-headline">Tím dnes</h2>
        {liveCount > 0 ? (
          <span className="status-chip status-chip-live shrink-0">{liveCount} live</span>
        ) : null}
      </div>
      {projectsQuery.isLoading || usersQuery.isLoading ? (
        <SkeletonCards count={3} />
      ) : (projectsQuery.isError && !projectsQuery.data) ||
        (usersQuery.isError && !usersQuery.data) ? (
        // "Nikto dnes nič nemá" by pri výpadku tvrdilo niečo, čo nevieme.
        <div className="panel">
          <QueryError
            compact
            title="Tím sa nepodarilo načítať."
            error={projectsQuery.error ?? usersQuery.error}
            onRetry={() => {
              if (projectsQuery.isError) void projectsQuery.refetch();
              if (usersQuery.isError) void usersQuery.refetch();
            }}
            retrying={projectsQuery.isFetching || usersQuery.isFetching}
          />
        </div>
      ) : teamToday.length === 0 ? (
        <div className="panel">
          <EmptyState
            variant="team"
            title="Nikto dnes nič nemá"
            hint="Keď si niekto naplánuje deň v Pláne alebo spustí prácu, uvidíš to tu."
            action={
              <Link to="/run" className="btn-primary text-sm">
                Spustiť prácu
              </Link>
            }
          />
        </div>
      ) : (
        <div className="list-group">
          {teamToday.map((row) => {
            // Worker offline while their session "runs" → no live treatment:
            // frozen duration, amber dot, 'offline' chip. My own row always
            // renders live (I am obviously online when I look at it).
            const liveOffline =
              row.live !== null &&
              row.userId !== user?.id &&
              row.live.workerOnline === false &&
              Boolean(row.live.workerLastSeenAt);
            const lastSeenTitle =
              liveOffline && row.live?.workerLastSeenAt
                ? `naposledy videný ${formatRelativeTime(normalizeIso(row.live.workerLastSeenAt))}`
                : undefined;

            return (
              <Link
                key={row.userId}
                to={`/users/${row.userId}`}
                className="list-row list-row-tappable"
                title={lastSeenTitle}
              >
                {row.live ? (
                  // LIVE: replace the presence dot with a pinging green dot —
                  // or a static amber one when the worker dropped offline.
                  <span className="relative inline-grid shrink-0">
                    <Avatar name={row.name} src={row.avatarUrl} size="md" />
                    {liveOffline ? (
                      <span
                        className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-surface"
                        role="img"
                        aria-label="offline, session pozastavená"
                      />
                    ) : (
                      <span
                        className="live-dot absolute bottom-0 right-0 h-3 w-3 rounded-full bg-accent-500 ring-2 ring-surface"
                        role="img"
                        aria-label="práve pracuje"
                      />
                    )}
                  </span>
                ) : (
                  <Avatar
                    name={row.name}
                    src={row.avatarUrl}
                    size="md"
                    statusMode={row.statusMode}
                    statusColor={row.statusColor}
                    online={row.online}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5">
                    <span className="truncate font-semibold">{row.name}</span>
                    {row.userId === user?.id && (
                      <span className="status-chip status-chip-idle">ty</span>
                    )}
                  </p>
                  {row.live ? (
                    <p
                      className={`truncate text-[13px] font-semibold ${
                        liveOffline
                          ? 'text-amber-600 dark:text-amber-300'
                          : 'text-emerald-600 dark:text-emerald-300'
                      }`}
                    >
                      {row.live.projectName} ·{' '}
                      <span className="font-mono tabular-nums">
                        {formatDuration(
                          liveOffline && row.live.workerLastSeenAt
                            ? frozenDuration(row.live.startedAt, row.live.workerLastSeenAt)
                            : runningDuration(row.live.startedAt),
                        )}
                      </span>
                    </p>
                  ) : row.plan ? (
                    <p className="truncate text-[13px] text-slate-500 dark:text-slate-400">
                      <span className="font-mono tabular-nums">
                        {formatPlanTime(row.plan.startTime)}–{formatPlanTime(row.plan.endTime)}
                      </span>{' '}
                      · {row.plan.projectName}
                      {row.planCount > 1 ? ` (+${row.planCount - 1})` : ''}
                    </p>
                  ) : null}
                </div>
                {row.live ? (
                  liveOffline ? (
                    <span className="status-chip status-chip-archived shrink-0">offline</span>
                  ) : (
                    <span className="status-chip status-chip-live live-glow shrink-0">LIVE</span>
                  )
                ) : (
                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );

  const mine = (
    /* --- Moje dnešky ------------------------------------------------------ */
    <section className="rise space-y-2">
      <h2 className="px-1 t-headline">Moje dnešky</h2>
      {!mySession && myPlans.length === 0 && myTasks.length === 0 ? (
        <div className="panel">
          <EmptyState
            variant="tasks"
            title="Dnes nemáš žiadne úlohy ani plán"
            hint="Pridaj si ich v Pláne a budeš ich mať tu na očiach."
          />
        </div>
      ) : (
        <div className="list-group">
          {mySession && (
            <Link to="/run" className="list-row list-row-tappable">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
                <PlayIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{mySession.projectName}</p>
                <p className="text-[13px] font-semibold text-emerald-600 dark:text-emerald-300">
                  Bežiaca session ·{' '}
                  <span className="font-mono tabular-nums">
                    {formatDuration(runningDuration(mySession.startedAt))}
                  </span>
                </p>
              </div>
              <span className="shrink-0 text-[14px] font-medium text-brand-600 dark:text-brand-400">
                Spustenie
              </span>
            </Link>
          )}
          {myPlans.map((plan) => (
            <Link key={plan.id} to="/plan" className="list-row list-row-tappable">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                <CalendarDaysIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{plan.projectName}</p>
                <p className="truncate text-[13px] text-slate-500 dark:text-slate-400">
                  <span className="font-mono tabular-nums">
                    {formatPlanTime(plan.startTime)}–{formatPlanTime(plan.endTime)}
                  </span>
                  {plan.note ? ` · ${plan.note}` : ''}
                </p>
              </div>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
            </Link>
          ))}
          {myTasks.map((task) => {
            const overdue = task.dueAt ? new Date(task.dueAt).getTime() < now : false;
            return (
              <Link key={task.id} to="/plan?tab=zoznam" className="list-row list-row-tappable">
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                    overdue
                      ? 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300'
                      : 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300'
                  }`}
                >
                  <ClockIcon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{task.title}</p>
                  <p className="truncate text-[13px] text-slate-500 dark:text-slate-400">
                    {task.projectName || 'Bez projektu'}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-[12px] font-semibold ${
                    overdue
                      ? 'text-red-600 dark:text-red-300'
                      : 'text-amber-600 dark:text-amber-300'
                  }`}
                >
                  {overdue ? 'Po termíne · ' : ''}
                  {task.dueAt ? formatDueLabel(task.dueAt) : ''}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );

  const activity = (
    /* --- Aktivita teaser --------------------------------------------------- */
    <section className="rise space-y-2">
      <h2 className="px-1 t-headline">Aktivita</h2>
      <div className="list-group">
        {eventsQuery.isLoading ? (
          <p className="px-4 py-8 text-center text-[14px] text-slate-500 dark:text-slate-400">
            Načítavam…
          </p>
        ) : eventsQuery.isError && !eventsQuery.data ? (
          <QueryError
            compact
            title="Aktivitu sa nepodarilo načítať."
            error={eventsQuery.error}
            onRetry={eventsQuery.refetch}
            retrying={eventsQuery.isFetching}
          />
        ) : feedItems.length === 0 ? (
          <EmptyState
            variant="feed"
            title="Zatiaľ žiadna aktivita"
            hint="Sessions, úlohy a git pushe sa objavia tu."
          />
        ) : (
          feedItems.map((item) => <FeedRow key={item.id} item={item} />)
        )}
        <button
          type="button"
          onClick={onShowActivity}
          className="list-row list-row-tappable w-full justify-center text-[14px] font-medium text-brand-600 dark:text-brand-400"
        >
          Všetka aktivita
        </button>
      </div>
    </section>
  );

  return <DomovLayout summary={summary} team={team} mine={mine} activity={activity} />;
};

/* --- Tab Aktivita ----------------------------------------------------------- */

const PAGE_SIZE = 30;

// 5 filters instead of the old 8: tasks+plans merged, notes/expenses/handoff
// folded into „Ostatné".
const ACTIVITY_FILTERS: Array<{ key: string; label: string; types: string }> = [
  { key: 'all', label: 'Všetko', types: '' },
  {
    key: 'sessions',
    label: 'Sessions',
    types: 'session.started,session.stopped,session.git_push_eta',
  },
  {
    key: 'work',
    label: 'Úlohy a plány',
    types: 'task.created,task.updated,plan.created,plan.updated,plan.deleted',
  },
  { key: 'git', label: 'Git', types: 'git.push,git.pr_opened,git.pr_merged,git.pr_closed' },
  {
    key: 'other',
    label: 'Ostatné',
    types:
      'note.created,expense.created,handoff.requested,handoff.accepted,handoff.declined,handoff.cancelled',
  },
];

// Normalize the D1 createdAt the same way feed.tsx does before turning it into a Date.
const normalizeIso = (createdAt: string): string =>
  createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T') + 'Z';

const feedDayKey = (date: Date): string =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const feedDayLabel = (createdAt: string): string => {
  const date = new Date(normalizeIso(createdAt));
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (feedDayKey(date) === feedDayKey(today)) {
    return 'Dnes';
  }
  if (feedDayKey(date) === feedDayKey(yesterday)) {
    return 'Včera';
  }
  return date.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type DayGroup = { key: string; label: string; items: FeedItem[] };

const groupByDay = (items: FeedItem[]): DayGroup[] => {
  const groups: DayGroup[] = [];
  let current: DayGroup | null = null;

  for (const item of items) {
    const key = feedDayKey(new Date(normalizeIso(item.createdAt)));
    if (!current || current.key !== key) {
      current = { key, label: feedDayLabel(item.createdAt), items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }

  return groups;
};

const AktivitaTab = ({ refreshing = false }: { refreshing?: boolean }) => {
  const { user } = useAuth();
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [groupRelated, setGroupRelated] = useState(true);
  const [filterKey, setFilterKey] = useState<string>(ACTIVITY_FILTERS[0]?.key ?? 'all');
  const [items, setItems] = useState<FeedItem[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Guards stale responses when the filter changes mid-flight.
  const requestRef = useRef(0);

  const activeFilter = useMemo(
    () => ACTIVITY_FILTERS.find((filter) => filter.key === filterKey) ?? ACTIVITY_FILTERS[0],
    [filterKey],
  );
  const types = activeFilter?.types ? activeFilter.types : undefined;

  // Initial load + reset whenever the filter changes.
  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setLoading(true);
    setItems([]);
    setNextBefore(null);

    let cancelled = false;
    void api
      .eventsList({ types, limit: PAGE_SIZE })
      .then((data) => {
        if (cancelled || requestRef.current !== requestId) {
          return;
        }
        setItems(data.items);
        setNextBefore(data.nextBefore);
      })
      .catch(() => {
        if (cancelled || requestRef.current !== requestId) {
          return;
        }
        toast.error('Aktivitu sa nepodarilo načítať.');
      })
      .finally(() => {
        if (cancelled || requestRef.current !== requestId) {
          return;
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [types]);

  // Pull-to-refresh: this feed is paged manually (not react-query), so reload
  // the first page in place when the page-level refresh starts. No skeleton —
  // existing rows stay visible until fresh data lands.
  const wasRefreshingRef = useRef(false);
  useEffect(() => {
    if (refreshing && !wasRefreshingRef.current) {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      void api
        .eventsList({ types, limit: PAGE_SIZE })
        .then((data) => {
          if (requestRef.current !== requestId) {
            return;
          }
          setItems(data.items);
          setNextBefore(data.nextBefore);
        })
        .catch(() => {
          if (requestRef.current !== requestId) {
            return;
          }
          toast.error('Aktivitu sa nepodarilo načítať.');
        });
    }
    wasRefreshingRef.current = refreshing;
  }, [refreshing, types]);

  const loadOlder = useCallback(() => {
    if (nextBefore === null || loadingMore) {
      return;
    }
    const requestId = requestRef.current;
    setLoadingMore(true);
    void api
      .eventsList({ types, before: nextBefore, limit: PAGE_SIZE })
      .then((data) => {
        if (requestRef.current !== requestId) {
          return;
        }
        setItems((prev) => [...prev, ...data.items]);
        setNextBefore(data.nextBefore);
      })
      .catch(() => {
        if (requestRef.current !== requestId) {
          return;
        }
        toast.error('Staršiu aktivitu sa nepodarilo načítať.');
      })
      .finally(() => {
        if (requestRef.current !== requestId) {
          return;
        }
        setLoadingMore(false);
      });
  }, [nextBefore, loadingMore, types]);

  const groups = useMemo(
    () =>
      groupByDay(attentionOnly ? items.filter((item) => needsAttention(item, user?.id)) : items),
    [items, attentionOnly, user?.id],
  );

  // Cumulative row index per group, for the staggered entrance below.
  const groupOffsets = useMemo(() => {
    const offsets: number[] = [];
    let count = 0;
    for (const group of groups) {
      offsets.push(count);
      count += group.items.length;
    }
    return offsets;
  }, [groups]);

  // Gentle staggered rise for the first ~10 rows; later rows enter together.
  const staggerDelay = (index: number): string => `${Math.min(index, 10) * 35}ms`;

  return (
    <div className="space-y-5">
      <SegmentedControl
        ariaLabel="Filter aktivity"
        scroll
        items={ACTIVITY_FILTERS}
        value={filterKey}
        onChange={setFilterKey}
      />

      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={groupRelated}
            onChange={(e) => setGroupRelated(e.target.checked)}
          />
          Zoskupiť súvisiace udalosti
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={attentionOnly}
            onChange={(e) => setAttentionOnly(e.target.checked)}
          />
          Vyžaduje pozornosť
        </label>
      </div>
      {attentionOnly && !loading && !groups.length && (
        <p className="text-sm text-slate-500">
          V načítanej aktivite nie sú problémy testov ani adresné požiadavky. Staršie udalosti môžeš
          načítať nižšie.
        </p>
      )}
      {loading ? (
        <SkeletonCards count={3} />
      ) : items.length === 0 ? (
        <div className="panel">
          <EmptyState
            variant="feed"
            title="Zatiaľ žiadna aktivita"
            hint="Sessions, úlohy, plány a git pushe tímu sa objavia tu."
          />
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, groupIndex) => (
            <section key={group.key} className="space-y-2">
              <div
                className="rise vibrancy sticky top-2 z-10 -mx-1 rounded-full px-3 py-1 text-[12px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                style={{ animationDelay: staggerDelay(groupOffsets[groupIndex] ?? 0) }}
              >
                {group.label}
              </div>
              <div className="list-group">
                {(groupRelated
                  ? groupedActivity(group.items)
                  : group.items.map((item) => [item])
                ).map((related, itemIndex) => {
                  const item = related[0];
                  return (
                    // Wrapper carries the stagger + the divider (rows are no
                    // longer siblings, so `.list-row + .list-row` won't apply).
                    <div
                      key={item.id}
                      className="rise border-t border-black/[0.06] first:border-t-0 dark:border-white/[0.07]"
                      style={{
                        animationDelay: staggerDelay((groupOffsets[groupIndex] ?? 0) + itemIndex),
                      }}
                    >
                      {needsAttention(item, user?.id) && (
                        <span className="ml-4 mt-3 inline-block rounded-full bg-amber-500/15 px-2 py-1 text-xs text-amber-700 dark:text-amber-300">
                          Vyžaduje pozornosť
                        </span>
                      )}
                      <FeedRow item={item} />
                      {related.length > 1 && (
                        <details className="px-4 pb-3">
                          <summary className="cursor-pointer text-sm text-slate-500">
                            Ďalšie súvisiace udalosti: {related.length - 1}
                          </summary>
                          {related.slice(1).map((row) => (
                            <FeedRow key={row.id} item={row} />
                          ))}
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {nextBefore !== null && (
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={loadOlder}
              disabled={loadingMore}
            >
              {loadingMore ? 'Načítavam…' : 'Načítať staršie'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
